<script context="module" lang="ts">
  export type ConfirmModal = {
    title: string;
    message: string;
    confirmLabel: string;
  };
</script>

<script lang="ts">
  import WrapField from "$components/form/Fields/WrapField.svelte";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import CrossIconButton from "app/components/+blocks/Button/CrossIconButton.svelte";
  import Dialogue from "$components/+blocks/Dialogue/Dialogue.svelte";
  import { uploadToUploadcare } from "$utils/uploadcare";
  import { createEventDispatcher, getContext, onMount } from "svelte";
  import { Image, Spinner } from "$components/+blocks/OVIcon";
  import type { createFormStore } from "../store";

  export let name: string;
  export let label = "";
  export let labelHidden = false;
  export let omitDefaultBackground = false;
  export let backgroundColor: string | undefined = undefined;
  export let radius = "rounded-xl";
  export let borderClass = "border";
  export let validationRules: Record<string, any> = {};
  export let wrapperClassname = "";
  export let help = "";
  export let size = "size-[70px]";
  export let confirmDeleteModal: ConfirmModal | undefined = undefined;
  export let value = "";

  let uploading = false;
  let isDeleteModalOpen = false;

  const dispatch = createEventDispatcher();
  const formStore: ReturnType<typeof createFormStore> | undefined = getContext("form");
  let error: any;
  let ref: HTMLInputElement;

  async function uploadImage(event: Event & { currentTarget: EventTarget & HTMLInputElement }) {
    uploading = true;
    const file = await uploadToUploadcare(event.currentTarget);
    value = file.src;

    if (formStore) {
      formStore.setField(name, value);
    } else {
      dispatch("change", value);
    }
    uploading = false;
  }

  function deletePhoto() {
    if (ref?.value) {
      ref.value = "";
    }

    value = "";

    if (formStore) {
      formStore.setField(name, value);
    } else {
      dispatch("change", value);
    }

    isDeleteModalOpen = false;
  }

  function onRemoveClicked() {
    if (confirmDeleteModal) {
      isDeleteModalOpen = true;
    } else {
      deletePhoto();
    }
  }

  onMount(() => {
    if (formStore) {
      formStore.addRef(name, ref);
      error = $formStore?.errors[name];
      value = $formStore?.values[name];
    }
  });
</script>

<WrapField {label} {name} {wrapperClassname} {validationRules} {help} {labelHidden}>
  <div class="inline-block leading-[0]">
    <div
      class="group relative flex items-center justify-center overflow-hidden border-[0.5px] border-black/20 dark:border-white/10
        {size} {radius} {borderClass} {value ? backgroundColor : ''}"
    >
      {#if value}
        <Avatar mainImage={value} radius="rounded-none" size="size-full" cropPosition={null} />

        <div class="invisible group-hover:visible">
          <CrossIconButton
            a11yLabel="Remove image"
            variant="theme-agnostic"
            on:click={onRemoveClicked}
            size="large"
            position="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
      {:else if uploading}
        <div class="group relative flex size-full items-center justify-center overflow-hidden">
          <OVIcon
            class="color animate-spin text-gray-400/80 dark:text-gray-300"
            icon={Spinner}
            size="w-5"
          />
        </div>
      {:else}
        <div class="flex">
          {#if !omitDefaultBackground}
            <svg
              class="size-full text-gray-300"
              class:border-danger={!!error}
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          {/if}
          <div
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 group-hover:visible"
            class:invisible={!omitDefaultBackground}
          >
            <!-- icon that triggers input modal -->
            <IconButton
              variant={omitDefaultBackground ? "tertiary" : "theme-agnostic"}
              icon={Image}
              a11yLabel="Upload image"
              size="large"
              on:click={() => ref?.click()}
            />
          </div>
        </div>
      {/if}

      <!-- don't remove input as form expects it to always be present for validation -->
      <input
        {...$$restProps}
        bind:this={ref}
        on:change={(event) => uploadImage(event)}
        type="file"
        accept="image/*"
        class="hidden"
        data-test="photo-upload"
        capture
      />
    </div>
  </div>
</WrapField>

{#if confirmDeleteModal && isDeleteModalOpen}
  <Dialogue
    bind:open={isDeleteModalOpen}
    title={confirmDeleteModal.title}
    message={confirmDeleteModal.message}
    confirmLabel={confirmDeleteModal.confirmLabel}
    on:confirm={deletePhoto}
    danger
  />
{/if}
